import { Drawer } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import { useFilters } from "../../../common/hooks/useFilters";
import { FeatureFlag, useFeatureFlagEnabled } from "../../../common/posthog";
import { Artifact } from "../../../types/artifact";
import { useAuth } from "../../auth/AuthContext";
import { useSelectedGroups } from "../../auth/hooks/useSelectedGroups";
import { useChatStore } from "../stores/chatStore";
import { ChatActions, ChatState, RowSelectionMode } from "../stores/chatTypes";
import { hasConversationMessages } from "../utils/chat";
import { extractInteractionIds } from "../utils/filters";
import { buildInteractionFilter, hasInteractionsOrFilterChanged } from "../utils/filters";
import ChatControls from "./ChatControls";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import UserSettingsPanel from "./UserSettingsPanel";

interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
  selectedRows: Artifact[];
  rowSelectionMode: RowSelectionMode;
}

const NO_MESSAGES = 0;

type ChatStoreValue = ChatState & ChatActions;

interface UserSyncParams {
  permittedEmails: string[] | null;
  tenantId: string | null;
  setPermittedEmails: (emails: string[] | null) => void;
  setTenantId: (id: string | null) => void;
}

const useUserSync = ({ permittedEmails, tenantId, setPermittedEmails, setTenantId }: UserSyncParams): void => {
  useEffect(() => {
    setPermittedEmails(permittedEmails);
    setTenantId(tenantId);
  }, [permittedEmails, tenantId, setPermittedEmails, setTenantId]);
};

const usePreferencesLoader = (chatSettingsEnabled: boolean, loadPreferencesFromStorage: () => void): void => {
  useEffect(() => {
    if (chatSettingsEnabled) {
      loadPreferencesFromStorage();
    }
  }, [chatSettingsEnabled, loadPreferencesFromStorage]);
};

const useThinkingModeSync = (thinkingModeEnabled: boolean, setThinkingModeEnabled: (enabled: boolean) => void): void => {
  useEffect(() => {
    setThinkingModeEnabled(thinkingModeEnabled);
  }, [thinkingModeEnabled, setThinkingModeEnabled]);
};

const drawerSx = {
  ["& .MuiDrawer-paper"]: {
    width: { xs: "100%", sm: "50%" },
    boxSizing: "border-box",
    height: "100%",
    display: "flex",
    flexDirection: "column"
  }
} as const;

interface InitialContextLoaderParams {
  open: boolean;
  messageCount: number;
  loading: boolean;
  selectedRows: Artifact[];
  filters: ReturnType<typeof useFilters>["filters"];
  rowSelectionMode: RowSelectionMode;
  includedArtifactTypes: ChatStoreValue["includedArtifactTypes"];
  loadContext: ChatStoreValue["loadContext"];
}

const useInitialContextLoader = (params: InitialContextLoaderParams): void => {
  const { open, messageCount, loading, selectedRows, filters, rowSelectionMode, includedArtifactTypes, loadContext } = params;
  useEffect(() => {
    if (open && messageCount === NO_MESSAGES && !loading) {
      const interactionIds = extractInteractionIds(selectedRows);
      const contextFilter = buildInteractionFilter(filters, rowSelectionMode, includedArtifactTypes);
      loadContext(interactionIds, false, contextFilter);
    }
  }, [open, messageCount, loading, selectedRows, loadContext, filters, rowSelectionMode, includedArtifactTypes]);
};

interface ContextChangeWatcherParams {
  open: boolean;
  isContextLoaded: boolean;
  loading: boolean;
  selectedRows: Artifact[];
  lastSelectedContext: ChatStoreValue["lastSelectedContext"];
  hasConversation: boolean;
  resetChat: () => void;
  saveAndRefreshSession: () => void;
  filters: ReturnType<typeof useFilters>["filters"];
}

const useContextChangeWatcher = (params: ContextChangeWatcherParams): void => {
  const { open, isContextLoaded, loading, selectedRows, lastSelectedContext, hasConversation, resetChat, saveAndRefreshSession, filters } = params;
  useEffect(() => {
    if (!open || !isContextLoaded || loading) {
      return;
    }

    const currentIds = extractInteractionIds(selectedRows);
    const interactionsOrFilterChanged = hasInteractionsOrFilterChanged({
      currentIds,
      lastSelectedContext,
      filters
    });

    if (!interactionsOrFilterChanged) {
      return;
    }

    if (hasConversation) {
      saveAndRefreshSession();
      return;
    }

    resetChat();
  }, [open, isContextLoaded, loading, selectedRows, lastSelectedContext, hasConversation, resetChat, saveAndRefreshSession, filters]);
};

interface SidebarStateSyncParams {
  permittedEmails: string[] | null;
  tenantId: string | null;
  setPermittedEmails: (emails: string[] | null) => void;
  setTenantId: (id: string | null) => void;
  chatSettingsEnabled: boolean;
  loadPreferencesFromStorage: () => void;
  thinkingModeEnabled: boolean;
  setThinkingModeEnabled: (enabled: boolean) => void;
  initialLoader: InitialContextLoaderParams;
  changeWatcher: ContextChangeWatcherParams;
}

const useSidebarStateSync = (params: SidebarStateSyncParams): void => {
  useUserSync({
    permittedEmails: params.permittedEmails,
    tenantId: params.tenantId,
    setPermittedEmails: params.setPermittedEmails,
    setTenantId: params.setTenantId
  });
  usePreferencesLoader(params.chatSettingsEnabled, params.loadPreferencesFromStorage);
  useThinkingModeSync(params.thinkingModeEnabled, params.setThinkingModeEnabled);
  useInitialContextLoader(params.initialLoader);
  useContextChangeWatcher(params.changeWatcher);
};

interface CloseHandlerParams {
  hidePreviousSession: () => void;
  setDrawerOpen: (open: boolean) => void;
  onClose: () => void;
}

const useCloseHandler = ({ hidePreviousSession, setDrawerOpen, onClose }: CloseHandlerParams): (() => void) => {
  return (): void => {
    hidePreviousSession();
    setDrawerOpen(false);
    onClose();
  };
};

interface SidebarBodyProps {
  handleClose: () => void;
  loading: boolean;
  selectedRows: Artifact[];
  rowSelectionMode: RowSelectionMode;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const SidebarBody = ({ handleClose, loading, selectedRows, rowSelectionMode, inputRef }: SidebarBodyProps): React.ReactElement => (
  <>
    <ChatHeader onClose={handleClose} loading={loading} />
    <ChatMessages />
    <UserSettingsPanel
      selectedRows={selectedRows}
      loading={loading}
      rowSelectionMode={rowSelectionMode}
    />
    <FeatureFlag flag="exploratory-chat-settings" fallback={null}>
      <ChatControls
        selectedRows={selectedRows}
        loading={loading}
        rowSelectionMode={rowSelectionMode}
      />
    </FeatureFlag>
    <ChatInput loading={loading} inputRef={inputRef} />
  </>
);

interface BuildSidebarParams {
  store: ChatStoreValue;
  props: ChatSidebarProps;
  filters: ReturnType<typeof useFilters>["filters"];
  permittedEmails: string[] | null;
  tenantId: string | null;
  chatSettingsEnabled: boolean;
  thinkingModeEnabled: boolean;
  hasConversation: boolean;
}

const buildSidebarStateSyncParams = (input: BuildSidebarParams): SidebarStateSyncParams => {
  const { store, props, filters, permittedEmails, tenantId, chatSettingsEnabled, thinkingModeEnabled, hasConversation } = input;
  return {
    permittedEmails,
    tenantId,
    setPermittedEmails: store.setPermittedEmails,
    setTenantId: store.setTenantId,
    chatSettingsEnabled,
    loadPreferencesFromStorage: store.loadPreferencesFromStorage,
    thinkingModeEnabled,
    setThinkingModeEnabled: store.setThinkingModeEnabled,
    initialLoader: {
      open: props.open,
      messageCount: store.messages.length,
      loading: store.loading,
      selectedRows: props.selectedRows,
      filters,
      rowSelectionMode: props.rowSelectionMode,
      includedArtifactTypes: store.includedArtifactTypes,
      loadContext: store.loadContext
    },
    changeWatcher: {
      open: props.open,
      isContextLoaded: store.isContextLoaded,
      loading: store.loading,
      selectedRows: props.selectedRows,
      lastSelectedContext: store.lastSelectedContext,
      hasConversation,
      resetChat: store.resetChat,
      saveAndRefreshSession: store.saveAndRefreshSession,
      filters
    }
  };
};

const ChatSidebar = (props: ChatSidebarProps): React.ReactElement => {
  const store = useChatStore();
  const { filters } = useFilters();
  const { permittedEmails } = useSelectedGroups();
  const { tenantId } = useAuth();
  const chatSettingsEnabled = useFeatureFlagEnabled("exploratory-chat-settings");
  const thinkingModeEnabled = useFeatureFlagEnabled("extended-thinking-mode-in-chat");

  const inputRef = useRef<HTMLInputElement>(null);
  const hasConversation = useMemo(() => hasConversationMessages(store.messages), [store.messages]);

  useSidebarStateSync(
    buildSidebarStateSyncParams({
      store, props, filters, permittedEmails, tenantId, chatSettingsEnabled, thinkingModeEnabled, hasConversation
    })
  );

  const handleClose = useCloseHandler({
    hidePreviousSession: store.hidePreviousSession,
    setDrawerOpen: store.setDrawerOpen,
    onClose: props.onClose
  });

  return (
    <Drawer anchor="right" open={props.open} onClose={handleClose} sx={drawerSx}>
      <SidebarBody
        handleClose={handleClose}
        loading={store.loading}
        selectedRows={props.selectedRows}
        rowSelectionMode={props.rowSelectionMode}
        inputRef={inputRef}
      />
    </Drawer>
  );
};

export default ChatSidebar;
